import {
  ContextWorkbenchChangesetBase,
  OrgGuideFileVersionId,
  typedObjectFromEntries,
} from "@hex/common";
import { keyBy } from "lodash";

import { ORG_ID } from "../../../../orgs";
import { selectContextWorkbench } from "../../../../redux/slices/contextWorkbenchSlice";
import { RootState } from "../../../../redux/store";
import { createSelector } from "../../../../redux/utils/index.js";
import { WorkbenchDiffStatus } from "../../../workbench/state/diffStatus";
import { ContextWorkbenchChangesetFragment } from "../../queries/useInitContextWorkbenchChangeset.generated";
import { OrgGuideFileWithBaseVersionFragment } from "../../queries/useInitContextWorkbenchPublishBaseFiles.generated";
import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import {
  ContextWorkbenchFile,
  ContextWorkbenchFileId,
} from "../contextWorkbenchFile";
import { ContextWorkbenchPublish } from "../contextWorkbenchPublish";
import { calculateOrgGuideFileDiffStatus } from "../utils/diff";
import {
  getEditorFilesListNodeForAsyncPlaceholder,
  getEditorFilesListNodeForFile,
} from "../utils/editorFilesListNode";
import { fileComparator, getFileForEntity } from "../utils/file";
import {
  getContextWorkbenchFileNodeGroups,
  getContextWorkbenchFileNodeGroupsAsyncPlaceholder,
} from "../utils/sources";

type SliceState = Pick<RootState, "contextWorkbench">;

const selectPublishFiles = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    (s: SliceState) => selectContextWorkbench(s).publish.baseFiles,
  ],
  (changeset, baseFiles) => {
    if (!changeset.value || !baseFiles.value) {
      return [];
    }
    const orgGuideFileChangesByFileId = keyBy(
      changeset.value.orgGuideFileChanges,
      (
        change: ContextWorkbenchChangesetFragment["orgGuideFileChanges"][number],
      ) => change.orgGuideFile.id,
    );
    const changesetBase = changeset.value.base;
    const getBaseVersionForFile = (
      orgGuideFile: OrgGuideFileWithBaseVersionFragment,
    ):
      | OrgGuideFileWithBaseVersionFragment["latestVersion"]
      | OrgGuideFileWithBaseVersionFragment["draftVersion"] => {
      if (changesetBase === ContextWorkbenchChangesetBase.DRAFT) {
        return orgGuideFile.draftVersion;
      } else {
        return orgGuideFile.latestVersion;
      }
    };

    const files = baseFiles.value
      .map((baseFile) => {
        const maybeChange = orgGuideFileChangesByFileId[baseFile.id];
        if (!maybeChange) {
          const baseVersion = getBaseVersionForFile(baseFile);
          const file = getFileForEntity({
            type: ContextWorkbenchEntity.Type.OrgGuideFile,
            contents: baseVersion?.contentsText ?? null,
            filePath: baseFile.filePath,
            orgId: ORG_ID,
            orgGuideFileId: baseFile.id,
            orgGuideFileVersionId:
              baseVersion?.id ??
              OrgGuideFileVersionId.check(`#deleted:${baseFile.id}`),
            externalSource: baseVersion?.externalSource ?? null,
          });
          return {
            ...file,
            diff: {
              status: WorkbenchDiffStatus.UNMODIFIED,
              baseContents: baseVersion?.contentsText ?? null,
            },
            externalSource: baseVersion?.externalSource ?? null,
          };
        } else {
          const file: ContextWorkbenchFile.Entity = getFileForEntity({
            type: ContextWorkbenchEntity.Type.OrgGuideFile,
            contents: maybeChange.orgGuideFileVersion?.contentsText ?? null,
            filePath: maybeChange.orgGuideFile.filePath,
            orgId: ORG_ID,
            orgGuideFileId: maybeChange.orgGuideFile.id,
            orgGuideFileVersionId:
              maybeChange.orgGuideFileVersion?.id ??
              OrgGuideFileVersionId.check(
                `#deleted:${maybeChange.orgGuideFile.id}`,
              ),
            externalSource:
              maybeChange.orgGuideFileVersion?.externalSource ?? null,
          });
          const diff = calculateOrgGuideFileDiffStatus({
            baseVersion: maybeChange.baseOrgGuideFileVersion,
            headVersion: maybeChange.orgGuideFileVersion,
          });
          const baseContents =
            maybeChange.baseOrgGuideFileVersion?.contentsText ?? "";
          return {
            ...file,
            diff: { status: diff, baseContents },
            externalSource:
              maybeChange.orgGuideFileVersion?.externalSource ?? null,
          };
        }
      })
      .filter(
        // baseFiles also includes deleted files meaning we also need to filter out files that are deleted that have no diff
        (changes) =>
          !(
            changes.diff.status === WorkbenchDiffStatus.UNMODIFIED &&
            changes.contents == null
          ),
      )
      .sort(fileComparator);
    return files;
  },
);

const selectVisibleFiles = createSelector(
  [
    selectPublishFiles,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
  ],
  (files, changesFilter) => {
    return files.filter((file) =>
      changesFilter === ContextWorkbenchPublish.ChangesFilter.CHANGED
        ? file.diff.status !== WorkbenchDiffStatus.UNMODIFIED
        : true,
    );
  },
);

const selectPublishFilesById = createSelector([selectPublishFiles], (files) => {
  return typedObjectFromEntries(files.map((file) => [file.id, file]));
});

const selectSelectedPublishFile = createSelector(
  [
    selectPublishFilesById,
    (s: SliceState) => selectContextWorkbench(s).publish.selectedFileId,
  ],
  (filesById, selectedFileId) => {
    return selectedFileId ? (filesById[selectedFileId] ?? null) : null;
  },
);

const selectPublishFileListNodesBySource = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    selectVisibleFiles,
  ],
  (changeset, visibleFiles) => {
    if (!changeset.value) {
      const node = getEditorFilesListNodeForAsyncPlaceholder(
        ContextWorkbenchEntity.Type.OrgGuideFile,
        changeset.loading,
        changeset.error,
      );
      return getContextWorkbenchFileNodeGroupsAsyncPlaceholder(node);
    } else {
      const nodes = visibleFiles.map((file) => ({
        ...getEditorFilesListNodeForFile(file.id),
        externalSource: file.externalSource,
      }));

      return getContextWorkbenchFileNodeGroups(nodes);
    }
  },
);

const selectFirstVisibleFile = createSelector([selectVisibleFiles], (files) => {
  return (
    files.find((f) => f.diff.status !== WorkbenchDiffStatus.UNMODIFIED)?.id ??
    files[0]?.id ??
    null
  );
});

export const contextWorkbenchPublishSelectors = {
  selectIsPublishing: (s: SliceState) =>
    selectContextWorkbench(s).view === "publish",
  selectView: (s: SliceState) => selectContextWorkbench(s).publish.view,
  selectBaseFiles: (s: SliceState) =>
    selectContextWorkbench(s).publish.baseFiles,
  selectChangeset: (s: SliceState) =>
    selectContextWorkbench(s).publish.changeset,
  selectLoadedChangeset: (s: SliceState) =>
    contextWorkbenchPublishSelectors.selectChangeset(s).value,
  selectAskPreviewThread: (s: SliceState) =>
    selectContextWorkbench(s).publish.askPreviewThread,
  selectFiles: selectPublishFiles,
  selectVisibleFiles: selectVisibleFiles,
  selectFilesById: selectPublishFilesById,
  selectFileById: (fileId: ContextWorkbenchFileId) => (s: SliceState) =>
    selectPublishFilesById(s)[fileId],
  selectFirstVisibleFile: selectFirstVisibleFile,
  selectSelectedFile: selectSelectedPublishFile,
  selectFileListNodesBySource: selectPublishFileListNodesBySource,
};
